import React, { useState, useEffect, useMemo } from 'react';
import { 
  Users, 
  BookOpen, 
  Building, 
  Plus, 
  ChevronRight, 
  Search, 
  X,
  MoreVertical,
  Play,
  Eye,
  Edit,
  Archive,
  Clock,
  MessageSquare,
  Lightbulb,
  FileVideo,
  RefreshCw,
  CheckSquare,
  Map,
  Award,
  Heart,
  Shield
} from 'lucide-react';
import type { Perfil, Topico, Subtopico } from '../types';
import { EcadProcessSidebar } from './EcadProcessSidebar';

// Função para retornar métricas consistentes por perfil
export const getProfileMetrics = (nome: string) => {
  const norm = nome.toLowerCase();
  if (norm.includes('aluno')) {
    return { pendentes: 3, feedbacks: 5, sugestoes: 0 };
  }
  if (norm.includes('diretor')) {
    return { pendentes: 1, feedbacks: 0, sugestoes: 2 };
  }
  if (norm.includes('professor')) {
    return { pendentes: 0, feedbacks: 4, sugestoes: 1 };
  }
  if (norm.includes('administrador') || norm.includes('admin')) {
    return { pendentes: 5, feedbacks: 2, sugestoes: 4 };
  }
  if (norm.includes('coordenador')) {
    return { pendentes: 0, feedbacks: 0, sugestoes: 3 };
  }
  return { pendentes: 0, feedbacks: 0, sugestoes: 0 };
};

// Função para classificar o perfil e retornar as propriedades visuais da categoria
export const getProfileCategory = (nome: string, isInterno: boolean) => {
  const norm = nome.toLowerCase();
  if (isInterno || norm.includes('administrador') || norm.includes('admin')) {
    return {
      label: 'Sistema',
      color: '#7c3aed',
      bg: '#faf5ff',
      iconName: 'shield'
    };
  }
  if (norm.includes('prefeitura') || norm.includes('gestor') || norm.includes('estado') || norm.includes('rede') || norm.includes('municip')) {
    return {
      label: 'Regiões',
      color: '#0891b2',
      bg: '#ecfeff',
      iconName: 'map'
    };
  }
  if (norm.includes('diret') || norm.includes('direc')) {
    return {
      label: 'Direção Escolar',
      color: '#d97706',
      bg: '#fffbeb',
      iconName: 'award'
    };
  }
  if (norm.includes('coordenad') || norm.includes('secretar') || norm.includes('professor') || norm.includes('docente')) {
    return {
      label: 'Equipe Escolar',
      color: '#2563eb',
      bg: '#eff6ff',
      iconName: 'users'
    };
  }
  if (norm.includes('responsav') || norm.includes('aluno') || norm.includes('pais') || norm.includes('famil')) {
    return {
      label: 'Familiares',
      color: '#16a34a',
      bg: '#f0fdf4',
      iconName: 'heart'
    };
  }
  return {
    label: 'Externo',
    color: '#475569',
    bg: '#f8fafc',
    iconName: 'users'
  };
};

// Função para renderizar o ícone de categoria lucide correspondente
export const getCategoryIcon = (iconName: string, size = 12, color?: string) => {
  switch (iconName) {
    case 'shield':
      return <Shield size={size} style={{ color }} />;
    case 'map':
      return <Map size={size} style={{ color }} />;
    case 'award':
      return <Award size={size} style={{ color }} />;
    case 'users':
      return <Users size={size} style={{ color }} />;
    case 'heart':
      return <Heart size={size} style={{ color }} />;
    default:
      return <Users size={size} style={{ color }} />;
  }
};


interface EcadCatalogDashboardProps {
  perfis: Perfil[];
  searchQuery: string;
  setSearchQuery: (val: string) => void;
  setSelectedPerfilId: (id: string | null) => void;
  setShowPerfilModal: (val: boolean) => void;
  onTriggerAction?: (action: string, detail?: string) => void;
  globalProcStatus?: 'idle' | 'running' | 'stopping' | 'stopped';
  setGlobalProcStatus?: React.Dispatch<React.SetStateAction<'idle' | 'running' | 'stopping' | 'stopped'>>;
  globalProcProgress?: number;
  setGlobalProcProgress?: React.Dispatch<React.SetStateAction<number>>;
  globalProcTimer?: number;
  setGlobalProcTimer?: React.Dispatch<React.SetStateAction<number>>;
  globalProcLogs?: string[];
  setGlobalProcLogs?: React.Dispatch<React.SetStateAction<string[]>>;
  globalAutoShutdown?: boolean;
  setGlobalAutoShutdown?: React.Dispatch<React.SetStateAction<boolean>>;
  completedRoteiroIds?: string[];
  queuedRoteiros?: Subtopico[];
  activeRoteiro?: Subtopico | null;
}

interface KpiCardProps {
  icon: React.ReactNode;
  label: string;
  value: number;
  color: string;
  glowColor: string;
}

// Subcomponente de KPI compacto com fundo colorido e ícone grande
function KpiCard({ icon, label, value, color, glowColor }: KpiCardProps) {
  const isZero = value === 0;
  return (
    <div style={{
      background: isZero ? '#e2e8f0' : color,
      border: '1px solid transparent',
      borderRadius: '12px',
      padding: '12px 14px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      boxShadow: isZero ? 'none' : `0 4px 14px ${glowColor}`,
      opacity: isZero ? 0.45 : 1,
      transition: 'all 0.2s',
      flex: 1,
      minWidth: '130px',
      color: isZero ? '#64748b' : '#ffffff'
    }}>
      <div style={{ color: isZero ? '#94a3b8' : '#ffffff', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
        {icon}
      </div>
      <div>
        <div style={{ fontSize: '0.62rem', color: isZero ? '#94a3b8' : 'rgba(255, 255, 255, 0.8)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          {label}
        </div>
        <div style={{ fontSize: '1.2rem', fontWeight: 900, color: isZero ? '#64748b' : '#ffffff', lineHeight: 1.1 }}>
          {value}
        </div>
      </div>
    </div>
  );
}

export function EcadCatalogDashboard({
  perfis,
  searchQuery,
  setSearchQuery,
  setSelectedPerfilId,
  setShowPerfilModal,
  onTriggerAction,
  globalProcStatus = 'idle',
  setGlobalProcStatus = () => {},
  globalProcProgress = 0,
  setGlobalProcProgress = () => {},
  globalProcTimer = 0,
  setGlobalProcTimer = () => {},
  globalProcLogs = [],
  setGlobalProcLogs = () => {},
  globalAutoShutdown = true,
  setGlobalAutoShutdown = () => {},
  completedRoteiroIds = [],
  queuedRoteiros = [],
  activeRoteiro = null,
}: EcadCatalogDashboardProps) {
  const [activeMenuPerfilId, setActiveMenuPerfilId] = useState<string | null>(null);

  // Fechar dropdowns de menu ao clicar fora
  useEffect(() => {
    const handleOutsideClick = () => {
      setActiveMenuPerfilId(null);
    };
    window.addEventListener('click', handleOutsideClick);
    return () => window.removeEventListener('click', handleOutsideClick);
  }, []);

  // Calcular soma de métricas dos perfis
  const totalMetrics = useMemo(() => {
    let pendentes = 0;
    let feedbacks = 0;
    let sugestoes = 0;
    perfis.forEach(p => {
      const m = getProfileMetrics(p.nome);
      pendentes += m.pendentes;
      feedbacks += m.feedbacks;
      sugestoes += m.sugestoes;
    });
    return { pendentes, feedbacks, sugestoes };
  }, [perfis]);

  const totalRoteiros = useMemo(() => {
    return perfis.reduce((acc, p) => acc + (p.topicos || []).reduce((sum, t) => sum + (t.subtopicos?.length || 0), 0), 0);
  }, [perfis]);

  const handleStartProcess = () => {
    if (onTriggerAction) {
      onTriggerAction('processa-all');
    }
  };

  const handleStopContainers = () => {
    if (onTriggerAction) {
      onTriggerAction('acompanha');
    }
    if (setGlobalProcStatus) {
      setGlobalProcStatus('stopping');
      setTimeout(() => {
        setGlobalProcStatus('stopped');
      }, 2000);
    }
  };

  const handleReset = () => {
    if (setGlobalProcStatus) setGlobalProcStatus('idle');
    if (setGlobalProcProgress) setGlobalProcProgress(0);
    if (setGlobalProcTimer) setGlobalProcTimer(0);
    if (setGlobalProcLogs) setGlobalProcLogs([]);
  };

  const dropdownItemStyle = (isDanger = false): React.CSSProperties => ({
    width: '100%',
    padding: '8px 10px',
    background: 'none',
    border: 'none',
    textAlign: 'left',
    fontSize: '0.72rem',
    color: isDanger ? '#ef4444' : '#475569',
    cursor: 'pointer',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.15s'
  });

  const handleGlobalActionClick = (e: React.MouseEvent, action: string) => {
    e.stopPropagation();
    if (onTriggerAction) {
      onTriggerAction(action);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', width: '100%', animation: 'fadeIn 0.25s ease-out' }}>
      <style dangerouslySetInnerHTML={{ __html: '@keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }' }} />

      {/* Cabeçalho principal com busca e adicionar */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <h1 style={{ fontSize: '1.6rem', fontWeight: 800, color: '#0f172a', margin: 0, fontFamily: "'Outfit', sans-serif", letterSpacing: '-0.5px' }}>
            Catálogo Geral de <span style={{ color: '#7c3aed' }}>Treinamento</span>
          </h1>
          <p style={{ fontSize: '0.82rem', color: '#64748b', margin: 0 }}>
            Gerencie e explore os perfis de acesso, tópicos pedagógicos e roteiros de simulação do Aluno Conectado.
          </p>
        </div>

        {/* Campo de busca reduzido e botão novo perfil ao lado do título */}
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexShrink: 0 }}>
          <div style={{ position: 'relative', width: '220px' }}>
            <input
              id="ecad-catalog-search"
              name="ecad-catalog-search"
              autoComplete="off"
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Pesquisar..."
              style={{
                width: '100%',
                padding: '8px 12px 8px 32px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                background: '#ffffff',
                color: '#1e293b',
                fontSize: '0.8rem',
                outline: 'none',
                transition: 'all 0.2s',
                fontWeight: 500,
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.02)'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#7c3aed';
                e.currentTarget.style.boxShadow = '0 0 0 2px rgba(124, 58, 237, 0.1)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#e2e8f0';
                e.currentTarget.style.boxShadow = 'none';
              }}
            />
            <Search size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: '#94a3b8',
                  cursor: 'pointer',
                  padding: 0,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <X size={14} />
              </button>
            )}
          </div>

          <button
            onClick={() => setShowPerfilModal(true)}
            style={{
              background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
              border: 'none',
              borderRadius: '8px',
              padding: '8px 14px',
              fontSize: '0.78rem',
              fontWeight: 700,
              color: '#ffffff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.2s',
              boxShadow: '0 3px 8px rgba(124, 58, 237, 0.15)',
              flexShrink: 0
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 5px 12px rgba(124, 58, 237, 0.25)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 3px 8px rgba(124, 58, 237, 0.15)';
            }}
          >
            <Plus size={14} /> Novo Perfil
          </button>
        </div>
      </div>

      {/* Grid Layout: Main Content (Left) + Process Monitor Sidebar (Right) */}
      <div style={{ display: 'flex', gap: '20px', width: '100%', alignItems: 'flex-start' }}>
        
        {/* Coluna da Esquerda: KPIs (no topo) e Perfis de Usuários (abaixo) */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '20px' }}>
          
          {/* Grid de KPIs Compactos (Fundo sólido, ícones dobram de tamanho - size=36) */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '10px', width: '100%' }}>
            <KpiCard 
              icon={<Users size={36} />} 
              label="Perfis" 
              value={perfis.length} 
              color="#7c3aed" 
              glowColor="rgba(124, 58, 237, 0.15)" 
            />
            <KpiCard 
              icon={<BookOpen size={36} />} 
              label="Tópicos" 
              value={perfis.reduce((acc, p) => acc + (p.topicos?.length || 0), 0)} 
              color="#4f46e5" 
              glowColor="rgba(79, 70, 229, 0.15)" 
            />
            <KpiCard 
              icon={<Building size={36} />} 
              label="Roteiros" 
              value={totalRoteiros} 
              color="#3b82f6" 
              glowColor="rgba(59, 130, 246, 0.15)" 
            />
            <KpiCard 
              icon={<Clock size={36} />} 
              label="Pendentes" 
              value={totalMetrics.pendentes} 
              color="#ea580c" 
              glowColor="rgba(234, 88, 12, 0.15)" 
            />
            <KpiCard 
              icon={<MessageSquare size={36} />} 
              label="Feedbacks" 
              value={totalMetrics.feedbacks} 
              color="#0284c7" 
              glowColor="rgba(2, 132, 199, 0.15)" 
            />
            <KpiCard 
              icon={<Lightbulb size={36} />} 
              label="Sugestões" 
              value={totalMetrics.sugestoes} 
              color="#16a34a" 
              glowColor="rgba(22, 163, 74, 0.15)" 
            />
          </div>

          <div style={{ width: '100%', marginBottom: '24px' }}>
            <h3 style={{ fontSize: '0.95rem', fontWeight: 800, color: '#0f172a', margin: '0 0 16px 0', fontFamily: "'Outfit', sans-serif" }}>
              Perfis de Usuários
            </h3>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: '12px' }}>
              {perfis.map((p) => {
                const metrics = getProfileMetrics(p.nome);
                const category = getProfileCategory(p.nome, p.isInterno);
                const roteirosOfPerfil = p.topicos.flatMap(t => t.subtopicos || []);
                const roteiroIdsOfPerfil = roteirosOfPerfil.map(r => r.id);
                
                const isProfileActive = activeRoteiro !== null && roteiroIdsOfPerfil.includes(activeRoteiro.id);
                const hasRoteiros = roteiroIdsOfPerfil.length > 0;
                const isProfileCompleted = hasRoteiros && roteiroIdsOfPerfil.every(id => completedRoteiroIds.includes(id));
                const isProfileQueued = !isProfileActive && !isProfileCompleted && roteiroIdsOfPerfil.some(id => queuedRoteiros.some(qr => qr.id === id));
                const queuePosition = isProfileQueued ? queuedRoteiros.findIndex(qr => roteiroIdsOfPerfil.includes(qr.id)) + 1 : -1;
                
                const roteirosCount = (p.topicos || []).reduce((sum, t) => sum + (t.subtopicos?.length || 0), 0);
                
                return (
                  <div
                    key={p.id}
                    onClick={() => setSelectedPerfilId(p.id)}
                    style={{
                      background: '#ffffff',
                      border: isProfileActive 
                        ? '1px solid #7c3aed' 
                        : isProfileCompleted 
                          ? '1px solid #10b981' 
                          : '1px solid #e2e8f0',
                      borderLeft: `4px solid ${category.color}`,
                      borderRadius: '12px',
                      padding: '16px',
                      cursor: 'pointer',
                      transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      minHeight: '145px',
                      boxShadow: isProfileActive 
                        ? '0 0 14px rgba(124, 58, 237, 0.25)' 
                        : '0 1px 3px rgba(0, 0, 0, 0.02), 0 1px 2px rgba(0, 0, 0, 0.01)',
                      position: 'relative',
                      animation: isProfileActive ? 'pulseBorder-anim 1.5s infinite alternate' : 'none'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = isProfileActive ? '#7c3aed' : category.color;
                      e.currentTarget.style.borderLeftColor = category.color;
                      e.currentTarget.style.boxShadow = isProfileActive 
                        ? '0 0 18px rgba(124, 58, 237, 0.35)' 
                        : `0 6px 16px rgba(0, 0, 0, 0.05), 0 4px 12px ${category.color}0d`;
                      e.currentTarget.style.transform = 'translateY(-3px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = isProfileActive 
                        ? '#7c3aed' 
                        : isProfileCompleted 
                          ? '#10b981' 
                          : '#e2e8f0';
                      e.currentTarget.style.borderLeftColor = category.color;
                      e.currentTarget.style.boxShadow = isProfileActive 
                        ? '0 0 14px rgba(124, 58, 237, 0.25)' 
                        : '0 1px 3px rgba(0, 0, 0, 0.02), 0 1px 2px rgba(0, 0, 0, 0.01)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    {/* Pulsing card keyframes */}
                    <style dangerouslySetInnerHTML={{ __html: `
                      @keyframes pulseBorder-anim {
                        from { border-color: #7c3aed; box-shadow: 0 0 4px rgba(124, 58, 237, 0.2); }
                        to { border-color: #a78bfa; box-shadow: 0 0 14px rgba(124, 58, 237, 0.45); }
                      }
                      @keyframes pulseText-anim {
                        from { opacity: 0.6; }
                        to { opacity: 1; }
                      }
                      .pulse-active-badge {
                        animation: pulseText-anim 1s infinite alternate;
                      }
                    `}} />
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span style={{
                            background: category.bg,
                            color: category.color,
                            padding: '3px 8px',
                            borderRadius: '9999px',
                            fontSize: '0.62rem',
                            fontWeight: 700,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.3px',
                            border: `1px solid ${category.color}15`
                          }}>
                            {getCategoryIcon(category.iconName, 10, category.color)}
                            {category.label}
                          </span>

                          {isProfileActive && (
                            <span className="pulse-active-badge" style={{
                              background: 'rgba(124, 58, 237, 0.08)',
                              color: '#7c3aed',
                              border: '1px solid rgba(124, 58, 237, 0.2)',
                              padding: '2px 6px',
                              borderRadius: '6px',
                              fontSize: '0.58rem',
                              fontWeight: 800
                            }}>
                              ● ATIVO
                            </span>
                          )}
                          {isProfileQueued && (
                            <span style={{
                              background: '#eff6ff',
                              color: '#1d4ed8',
                              border: '1px solid #bfdbfe',
                              padding: '2px 6px',
                              borderRadius: '6px',
                              fontSize: '0.58rem',
                              fontWeight: 800
                            }}>
                              ⏳ FILA [{queuePosition}]
                            </span>
                          )}
                          {isProfileCompleted && (
                            <span style={{
                              background: '#f0fdf4',
                              color: '#15803d',
                              border: '1px solid #bbf7d0',
                              padding: '2px 6px',
                              borderRadius: '6px',
                              fontSize: '0.58rem',
                              fontWeight: 800
                            }}>
                              ✅ CONCLUÍDO
                            </span>
                          )}
                        </div>
                        
                        <div style={{ position: 'relative' }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveMenuPerfilId(activeMenuPerfilId === p.id ? null : p.id);
                            }}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#94a3b8',
                              cursor: 'pointer',
                              padding: '4px',
                              borderRadius: '6px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.15s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#f1f5f9'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                          >
                            <MoreVertical size={16} />
                          </button>

                          {activeMenuPerfilId === p.id && (
                            <div 
                              style={{
                                position: 'absolute',
                                top: '100%',
                                right: 0,
                                background: '#ffffff',
                                border: '1px solid #e2e8f0',
                                borderRadius: '8px',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                                zIndex: 100,
                                minWidth: '160px',
                                padding: '4px',
                                marginTop: '4px'
                              }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <button
                                onClick={() => {
                                  onTriggerAction?.('processar-perfil', p.nome);
                                  setActiveMenuPerfilId(null);
                                }}
                                style={dropdownItemStyle()}
                                onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                                onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                              >
                                <Play size={12} style={{ color: '#7c3aed' }} /> Processar Perfil
                              </button>
                              <button
                                onClick={() => {
                                  onTriggerAction?.('acompanhar-perfil', p.nome);
                                  setActiveMenuPerfilId(null);
                                }}
                                style={dropdownItemStyle()}
                                onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                                onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                              >
                                <Eye size={12} style={{ color: '#3b82f6' }} /> Ver Logs
                              </button>
                              <button
                                onClick={() => {
                                  onTriggerAction?.('editar-perfil', p.nome);
                                  setActiveMenuPerfilId(null);
                                }}
                                style={dropdownItemStyle()}
                                onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                                onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                              >
                                <Edit size={12} style={{ color: '#10b981' }} /> Editar Nome
                              </button>
                              <button
                                onClick={() => {
                                  onTriggerAction?.('arquivar-perfil', p.nome);
                                  setActiveMenuPerfilId(null);
                                }}
                                style={dropdownItemStyle(true)}
                                onMouseEnter={(e) => e.currentTarget.style.background = '#fef2f2'}
                                onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                              >
                                <Archive size={12} /> Arquivar
                              </button>
                            </div>
                          )}
                        </div>
                      </div>

                      <h4 style={{
                        fontSize: '0.98rem',
                        fontWeight: 800,
                        color: '#0f172a',
                        margin: '2px 0 8px 0',
                        fontFamily: "'Outfit', sans-serif",
                        letterSpacing: '-0.3px'
                      }}>
                        {p.nome}
                      </h4>

                      <div style={{ display: 'flex', gap: '6px', marginTop: '6px', flexWrap: 'wrap' }}>
                        {metrics.pendentes > 0 && (
                          <span style={{
                            background: '#fff7ed',
                            border: '1px solid #ffedd5',
                            color: '#c2410c',
                            padding: '2px 6px',
                            borderRadius: '6px',
                            fontSize: '0.62rem',
                            fontWeight: 700,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}>
                            <Clock size={10} style={{ color: '#ea580c' }} />
                            {metrics.pendentes} pendente{metrics.pendentes > 1 ? 's' : ''}
                          </span>
                        )}
                        {metrics.feedbacks > 0 && (
                          <span style={{
                            background: '#e0f2fe',
                            border: '1px solid #bae6fd',
                            color: '#0369a1',
                            padding: '2px 6px',
                            borderRadius: '6px',
                            fontSize: '0.62rem',
                            fontWeight: 700,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}>
                            <MessageSquare size={10} style={{ color: '#0284c7' }} />
                            {metrics.feedbacks} feedback{metrics.feedbacks > 1 ? 's' : ''}
                          </span>
                        )}
                        {metrics.sugestoes > 0 && (
                          <span style={{
                            background: '#f0fdf4',
                            border: '1px solid #dcfce7',
                            color: '#15803d',
                            padding: '2px 6px',
                            borderRadius: '6px',
                            fontSize: '0.62rem',
                            fontWeight: 700,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}>
                            <Lightbulb size={10} style={{ color: '#16a34a' }} />
                            {metrics.sugestoes} sugestão{metrics.sugestoes > 1 ? 'ões' : ''}
                          </span>
                        )}
                      </div>
                    </div>

                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      fontSize: '0.72rem',
                      color: '#64748b',
                      marginTop: '16px',
                      paddingTop: '10px',
                      borderTop: '1px solid #f1f5f9'
                    }}>
                      <span style={{ fontWeight: 500 }}>
                        📂 <strong>{(p.topicos || []).length}</strong> tópicos • 📝 <strong>{roteirosCount}</strong> roteiros
                      </span>
                      <div style={{
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        background: `${category.color}0c`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s'
                      }}>
                        <ChevronRight size={12} style={{ color: category.color }} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Coluna da Direita: Painel de Processamento & Auditoria */}
        <EcadProcessSidebar
          title="Processamento Geral"
          subtitle="Monitore e execute as automações"
          type="perfil"
          name="Geral"
          totalRoteiros={totalRoteiros}
          pendingCount={totalMetrics.pendentes}
          onTriggerAction={onTriggerAction}
          status={globalProcStatus}
          setStatus={setGlobalProcStatus}
          progress={globalProcProgress}
          setProgress={setGlobalProcProgress}
          timer={globalProcTimer}
          setTimer={setGlobalProcTimer}
          logs={globalProcLogs}
          setLogs={setGlobalProcLogs}
          autoShutdown={globalAutoShutdown}
          setAutoShutdown={setGlobalAutoShutdown}
          handleStartProcess={handleStartProcess}
          handleStopContainers={handleStopContainers}
          handleReset={handleReset}
        />

      </div>
    </div>
  );
}
